import { StepLayout } from '@/components/NewGeneralDialog/StepLayout';
import { WizardAnimationWrapper } from '@/components/WizardAnimationWrapper';
import { Documents } from '@/types/company';
import { Box, Button, Stack, TextField, Typography } from '@mui/material';
import { Col } from '@treyd-io/treyd-ui/components/Grid/Col';
import { Row } from '@treyd-io/treyd-ui/components/Grid/Row';
import ArrowRightIcon from '@treyd-io/treyd-ui/icons/ArrowRightIcon';
import { Form, Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { NumericFormat } from 'react-number-format';
import * as Yup from 'yup';

interface LiabilitiesStepProps {
  formData: Partial<Documents.DocumentType['data']>;
  currency: string;
  onMovingForward: (values: Partial<Documents.DocumentType['data']>) => void;
  onMovingBackward: () => void;
  next?: boolean;
}

export const LiabilitiesStep = ({
  formData,
  currency,
  onMovingForward,
  onMovingBackward,
  next,
}: LiabilitiesStepProps) => {
  const { t } = useTranslation();

  const liabilitiesCurrency = formData?.Liabilities_Currency || currency;

  const initialValues = {
    home_loan: formData?.Home_Loan || '',
    investment_loans: formData?.Investment_Loans || '',
    auto_loans: formData?.Auto_Loans || '',
    other_loans: formData?.Other_Loans || '',
    credit_cards: formData?.Credit_cards || '',
  };

  const validationSchema = Yup.object({
    home_loan: Yup.string().required('Required'),
    investment_loans: Yup.string().required('Required'),
    auto_loans: Yup.string().required('Required'),
    other_loans: Yup.string().required('Required'),
    credit_cards: Yup.string().required('Required'),
  });

  const handleSubmit = (values: typeof initialValues) => {
    onMovingForward({
      Liabilities_Currency: liabilitiesCurrency,
      Home_Loan: values.home_loan,
      Investment_Loans: values.investment_loans,
      Other_Loans: values.other_loans,
      Auto_Loans: values.auto_loans,
      Credit_cards: values.credit_cards,
    });
  };

  return (
    <WizardAnimationWrapper next={next}>
      <StepLayout
        onMovingBackward={onMovingBackward}
        containerProps={{
          gap: 1,
        }}>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          validateOnMount>
          {(formik) => (
            <Form>
              <Stack width="100%" gap={3}>
                <Box>
                  <Typography
                    variant="h4"
                    color="text.primary"
                    marginBottom={1}>
                    {t('liabilities')}
                  </Typography>
                  <Typography variant="body1" color="text.primary">
                    {t('liabilities_subtitle')}
                  </Typography>
                </Box>

                <Row rowSpacing={3}>
                  <Col xs={12} sm={6}>
                    <NumericFormat
                      fullWidth
                      customInput={TextField}
                      label={t('home_loan')}
                      name="home_loan"
                      type="text"
                      value={formik.values.home_loan}
                      onChange={formik.handleChange}
                      error={
                        formik.touched.home_loan && !!formik.errors.home_loan
                      }
                      slotProps={{
                        input: {
                          endAdornment: (
                            <Typography variant="body2" color="text.disabled">
                              {liabilitiesCurrency}
                            </Typography>
                          ),
                        },
                        htmlInput: {
                          min: 0,
                        },
                      }}
                      helperText={
                        formik.touched.home_loan && formik.errors.home_loan
                      }
                      thousandSeparator={' '}
                      decimalSeparator=","
                      allowedDecimalSeparators={[',', '.']}
                      decimalScale={2}
                      fixedDecimalScale={true}
                    />
                  </Col>
                  <Col xs={12} sm={6}>
                    <NumericFormat
                      fullWidth
                      customInput={TextField}
                      label={t('investment_loans')}
                      name="investment_loans"
                      type="text"
                      value={formik.values.investment_loans}
                      onChange={formik.handleChange}
                      error={
                        formik.touched.investment_loans &&
                        !!formik.errors.investment_loans
                      }
                      slotProps={{
                        input: {
                          endAdornment: (
                            <Typography variant="body2" color="text.disabled">
                              {liabilitiesCurrency}
                            </Typography>
                          ),
                        },
                        htmlInput: {
                          min: 0,
                        },
                      }}
                      helperText={
                        formik.touched.investment_loans &&
                        formik.errors.investment_loans
                      }
                      thousandSeparator={' '}
                      decimalSeparator=","
                      allowedDecimalSeparators={[',', '.']}
                      decimalScale={2}
                      fixedDecimalScale={true}
                    />
                  </Col>
                </Row>
                <Row rowSpacing={3}>
                  <Col xs={12} sm={6}>
                    <NumericFormat
                      fullWidth
                      customInput={TextField}
                      label={t('auto_loans')}
                      name="auto_loans"
                      type="text"
                      value={formik.values.auto_loans}
                      onChange={formik.handleChange}
                      error={
                        formik.touched.auto_loans && !!formik.errors.auto_loans
                      }
                      slotProps={{
                        input: {
                          endAdornment: (
                            <Typography variant="body2" color="text.disabled">
                              {liabilitiesCurrency}
                            </Typography>
                          ),
                        },
                        htmlInput: {
                          min: 0,
                        },
                      }}
                      helperText={
                        formik.touched.auto_loans && formik.errors.auto_loans
                      }
                      thousandSeparator={' '}
                      decimalSeparator=","
                      allowedDecimalSeparators={[',', '.']}
                      decimalScale={2}
                      fixedDecimalScale={true}
                    />
                  </Col>
                  <Col xs={12} sm={6}>
                    <NumericFormat
                      fullWidth
                      customInput={TextField}
                      label={t('other_loans')}
                      name="other_loans"
                      type="text"
                      value={formik.values.other_loans}
                      onChange={formik.handleChange}
                      error={
                        formik.touched.other_loans &&
                        !!formik.errors.other_loans
                      }
                      slotProps={{
                        input: {
                          endAdornment: (
                            <Typography variant="body2" color="text.disabled">
                              {liabilitiesCurrency}
                            </Typography>
                          ),
                        },
                        htmlInput: {
                          min: 0,
                        },
                      }}
                      helperText={
                        formik.touched.other_loans && formik.errors.other_loans
                      }
                      thousandSeparator={' '}
                      decimalSeparator=","
                      allowedDecimalSeparators={[',', '.']}
                      decimalScale={2}
                      fixedDecimalScale={true}
                    />
                  </Col>
                </Row>
                <Row rowSpacing={3}>
                  <Col xs={12} sm={6}>
                    <NumericFormat
                      fullWidth
                      customInput={TextField}
                      label={t('credit_cards')}
                      name="credit_cards"
                      type="text"
                      value={formik.values.credit_cards}
                      onChange={formik.handleChange}
                      error={
                        formik.touched.credit_cards &&
                        !!formik.errors.credit_cards
                      }
                      slotProps={{
                        input: {
                          endAdornment: (
                            <Typography variant="body2" color="text.disabled">
                              {liabilitiesCurrency}
                            </Typography>
                          ),
                        },
                        htmlInput: {
                          min: 0,
                        },
                      }}
                      helperText={
                        formik.touched.credit_cards &&
                        formik.errors.credit_cards
                      }
                      thousandSeparator={' '}
                      decimalSeparator=","
                      allowedDecimalSeparators={[',', '.']}
                      decimalScale={2}
                      fixedDecimalScale={true}
                    />
                  </Col>
                </Row>

                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  type="submit"
                  endIcon={<ArrowRightIcon />}
                  disabled={!formik.isValid}
                  sx={{ width: { sm: 'fit-content' }, marginTop: 0.5 }}>
                  {t('continue')}
                </Button>
              </Stack>
            </Form>
          )}
        </Formik>
      </StepLayout>
    </WizardAnimationWrapper>
  );
};
